/**
 * @module app.shared
 */

import { BindingEngine, Disposable, autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { oneOf } from 'app/shared/lib/utilities';
import { getKey, Key } from 'app/shared/lib/keys';
import { guid } from 'app/shared/lib/guid';
import { escapeHtml, IHtmlValue, joinWithLineBreaks } from 'app/shared/lib/formatting/html-utilities';
import { isElementClickable, isElementInteractive, setAttributeIfNotExist, setAttributesIfNotExist } from 'app/shared/lib/dom';

type MaterializeTooltip = M.Tooltip & {
    tooltipEl: Element
}

@autoinject
export class EssTooltipCustomAttribute {

    ////****************** Fields and Properties ************************************////
    @bindable.any({ primaryProperty: true, defaultBindingMode: bindingMode.toView, changeHandler: 'textChanged' })
    public text: any = '';

    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'positionChanged' })
    public position: string = 'bottom';

    @bindable.booleanAttr({ defaultBindingMode: bindingMode.toView })
    public isDescribedBy: boolean = false;

    private tooltip: MaterializeTooltip | null = null;
    private isAttached: boolean = false;
    private isActive: boolean = false;

    private guid = guid();
    private tooltipId: string = `${this.guid}-tooltip`;

    protected tooltipOpenSubscription: Disposable | null = null;

    ////****************** Element References ***************************************////
    private $tooltipContent: Element | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(protected readonly element: Element, private readonly bindingEngine: BindingEngine) { }

    protected attached(): void {
        this.isAttached = true;
        this.updateTooltip();

        this.element.addEventListener("keydown", this.handleKeyDown.bind(this));
        this.element.addEventListener("mouseover", this.handleMouseOver.bind(this));
        this.element.addEventListener("mouseleave", this.handleMouseLeave.bind(this), { capture: true });
    }

    protected detached(): void {
        try {
            this.destroy();
            this.element.removeEventListener("keydown", this.handleKeyDown.bind(this));
            this.element.removeEventListener("mouseover", this.handleMouseOver.bind(this));
            this.element.removeEventListener("mouseleave", this.handleMouseLeave.bind(this), { capture: true });
        }
        finally {
            this.isAttached = false;
        }
    }

    ////****************** Functions ************************************************////
    protected destroy() {
        if(this.tooltip) {
            this.element.classList.remove('ess-tooltip');
            this.$tooltipContent?.removeEventListener("mouseover", this.handleTooltipMouseOver.bind(this), { capture: true });
            this.$tooltipContent?.removeEventListener("mouseleave", this.handleTooltipMouseLeave.bind(this), { capture: true });
            this.tooltip.destroy();
            this.tooltip = null;
        }
    }

    protected textChanged(newValue: string, oldValue: string): void {
        if(newValue !== oldValue) {
            this.updateTooltip();
        }
    }

    protected positionChanged(newValue: string, oldValue: string): void {
        if(newValue !== oldValue) {
            this.updateTooltip();
        }
    }

    private updateTooltip(): void {
        if(!this.isAttached) { return; }
        this.destroy();

        if(this.text) {
            let toolTipHtml: IHtmlValue | null;
            if(Array.isArray(this.text) && this.text.length > 0) {
                toolTipHtml = joinWithLineBreaks(this.text);
            }
            else {
                toolTipHtml = escapeHtml(this.text);
            }
            if(!toolTipHtml) { return; }

            this.element.classList.add('ess-tooltip');
            const options: Partial<M.TooltipOptions> = {
                html: toolTipHtml.html,
                enterDelay: 500,
                exitDelay: 100,
                inDuration: 100,
                outDuration: 500,
                margin: 2,
                transitionMovement: 5,
                position: oneOf(this.position, ['bottom', 'top', 'left', 'right'])
            };
            this.tooltip = M.Tooltip.init(this.element, options) as MaterializeTooltip;

            // Observe Changes to the Tooltip's `isOpen` property
            this.tooltipOpenSubscription = this.bindingEngine
                .propertyObserver(this.tooltip, 'isOpen')
                .subscribe((newValue, oldValue) => {
                    this.tooltipOpenChanged(newValue, oldValue);
                });

            if(this.tooltip.tooltipEl) {
                this.$tooltipContent = this.tooltip.tooltipEl.querySelector('.tooltip-content');
            }

            this.tooltipId = this.$tooltipContent?.id || (this.element.id ? `${this.element.id}-tooltip` : `${this.guid}-tooltip`);

            if(this.$tooltipContent) {
                setAttributesIfNotExist(this.$tooltipContent, { 'id': this.tooltipId, 'role': 'tooltip' })
            }
            this.$tooltipContent?.addEventListener("mouseover", this.handleTooltipMouseOver.bind(this), { capture: true });
            this.$tooltipContent?.addEventListener("mouseleave", this.handleTooltipMouseLeave.bind(this), { capture: true });

            this.updateElement(toolTipHtml);
        }
    }

    // Update the element that owns the Tooltip
    private updateElement(toolTipHtml: IHtmlValue | null): void {
        const isClickable: boolean = isElementClickable(<HTMLElement>this.element);
        const isInteractive: boolean = isElementInteractive(<HTMLElement>this.element);

        if(!isClickable && !isInteractive && toolTipHtml) {
            // For non-interactive elements create a screen reader only element that contains the tooltip text
            const screenReaderElement = document.createElement('div');
            screenReaderElement.classList.add('screen-reader-only');
            screenReaderElement.innerHTML = toolTipHtml.html;
            this.element.insertAdjacentElement('afterend', screenReaderElement);
        }

        // If the element has user interactions, ensure the element is tabbable
        if(isClickable) {
            setAttributeIfNotExist(this.element, 'tabIndex', '0');
        }

        // If the element has user interactions, set a button role when it is required
        if(isClickable && !isInteractive) {
            setAttributeIfNotExist(this.element, 'role', 'button');
        }

        // If the element has user interactions, set a screen reader description
        if(isClickable && !this.isDescribedBy && !(this.element.getAttribute('aria-label') || this.element.getAttribute('aria-labelledby'))) {
            this.element.setAttribute('aria-labelledby', this.tooltipId);
        }

        // Set if the tooltip should use an aria-describedby
        if(this.isDescribedBy) {
            this.element.setAttribute('aria-describedby', this.tooltipId);
        }
    }

    /** Handles changes to the Tooltip's `isOpen` property */
    protected tooltipOpenChanged(newValue: any, oldValue: any): void {
        if(!this.tooltip) { return; }
        if(newValue) {
            // Tooltip Is Open\Visible
            this.tooltip.tooltipEl.classList.remove('tooltip-hidden');
        }
        else {
            this.tooltip.tooltipEl.classList.add('tooltip-hidden');
        }
    }

    protected handleKeyDown(event: KeyboardEvent): boolean {
        const key = getKey(event);
        switch(key) {
            case Key.Esc:
                this.closeTooltip();
                return false;
            default:
                return true;
        }
    }

    protected handleMouseOver(event: MouseEvent): void {
        this.isActive = true;
    }

    protected handleMouseLeave(event: MouseEvent): void {
        event.stopImmediatePropagation();
        this.isActive = false;
        setTimeout(this.closeTooltip.bind(this), 200);
    }

    protected handleTooltipMouseOver(event: MouseEvent): void {
        //mouse moves away from the element and enters the tooltip
        this.isActive = true;
    }

    protected handleTooltipMouseLeave(event: MouseEvent): void {
        this.isActive = false;
        if(this.tooltip?.isOpen) { setTimeout(this.closeTooltip.bind(this), 200); }
    }

    protected closeTooltip(): void {
        if(!this.isActive) {
            this.tooltip?.close();
        }
    }

}
